import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useCallback, useState } from "react";

import { cn } from "../../utils/cn";
import { Button } from "../button/Button";
import { Popover } from "../popover/Popover";
import { Text } from "../typography/Text";

import type { ReactNode, Ref } from "react";
import type { UsePopoverProps } from "../popover/usePopover";

const DEFAULT_ICON = <FontAwesomeIcon icon={faExclamationCircle} className="text-yellow" />;

export interface ConfirmPopoverProps
  extends Pick<
    UsePopoverProps,
    | "placement"
    | "offset"
    | "crossOffset"
    | "shouldFlip"
    | "showArrow"
    | "portalContainer"
    | "defaultOpen"
  > {
  /** The trigger element that opens the confirmation popover. */
  children: ReactNode;
  /** Title text shown at the top of the confirmation popover. */
  title: ReactNode;
  /** Optional description text shown below the title. */
  description?: ReactNode;
  /**
   * Icon shown to the left of the title. Set to `null` to hide.
   * @default Warning circle icon (amber)
   */
  icon?: ReactNode | null;
  /** Text for the confirm button. @default "Yes" */
  confirmText?: string;
  /** Text for the cancel button. @default "No" */
  cancelText?: string;
  /** Whether the confirm action is destructive (styles the confirm button red). */
  destructive?: boolean;
  /** Called when the user clicks the confirm button. */
  onConfirm?: () => void;
  /** Called when the user clicks the cancel button or dismisses the popover. */
  onCancel?: () => void;
  /** Whether the confirm button is in a loading state. */
  isLoading?: boolean;
  /** Whether the confirm button is disabled. */
  isConfirmDisabled?: boolean;
  /** Controlled open state. When provided, the component is fully controlled. */
  open?: boolean;
  /** Callback when open state changes. Required for controlled mode. Also fires in uncontrolled mode. */
  onOpenChange?: (open: boolean) => void;
  /** Additional class name for the popover content container. */
  className?: string;
}

/**
 * An inline anchored confirmation popover that appears near its trigger element.
 * Use this for lightweight confirmations (e.g., "Reboot device?", "Delete item?")
 * where a full-screen AlertDialog modal would be too disruptive.
 *
 * This is the rivet-ui replacement for antd's `Popconfirm` component.
 *
 * Supports both controlled and uncontrolled usage:
 *
 * @example Uncontrolled (simplest, like antd Popconfirm)
 * ```tsx
 * <ConfirmPopover
 *   title="Are you sure you want to reboot?"
 *   onConfirm={handleReboot}
 * >
 *   <Button>Reboot</Button>
 * </ConfirmPopover>
 * ```
 *
 * @example Controlled
 * ```tsx
 * <ConfirmPopover
 *   title="Delete this item?"
 *   description="This action cannot be undone."
 *   destructive
 *   confirmText="Delete"
 *   cancelText="Cancel"
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   onConfirm={handleDelete}
 * >
 *   <Button>Delete</Button>
 * </ConfirmPopover>
 * ```
 */
export const ConfirmPopover = forwardRef<HTMLDivElement, ConfirmPopoverProps>((props, ref) => {
  const {
    children,
    title,
    description,
    icon = DEFAULT_ICON,
    confirmText = "Yes",
    cancelText = "No",
    destructive = false,
    onConfirm,
    onCancel,
    isLoading = false,
    isConfirmDisabled = false,
    className,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    defaultOpen,
    placement = "top",
    offset = 10,
    crossOffset,
    shouldFlip,
    showArrow = true,
    portalContainer,
  } = props;

  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false);

  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(nextOpen);
      }
      controlledOnOpenChange?.(nextOpen);
    },
    [isControlled, controlledOnOpenChange]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        onCancel?.();
      }
    },
    [setOpen, onCancel]
  );

  const handleConfirm = useCallback(() => {
    onConfirm?.();
    setOpen(false);
  }, [onConfirm, setOpen]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    setOpen(false);
  }, [onCancel, setOpen]);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      placement={placement}
      offset={offset}
      crossOffset={crossOffset}
      shouldFlip={shouldFlip}
      showArrow={showArrow}
      classNames={{ content: "p-0" }}
      portalContainer={portalContainer}
      ref={ref as Ref<HTMLDivElement>}
    >
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content>
        <div
          className={cn(
            "flex flex-col gap-3 p-4 text-left",
            "min-w-[200px] max-w-[300px]",
            className
          )}
        >
          <div className="flex items-start gap-2">
            {icon !== null && <span className="mt-px shrink-0 text-sm">{icon}</span>}
            <Text size="md" bold>
              {title}
            </Text>
          </div>
          {description && (
            <Text size="sm" emphasis="secondary">
              {description}
            </Text>
          )}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="default" onPress={handleCancel}>
              {cancelText}
            </Button>
            <Button
              size="sm"
              variant="primary"
              destructive={destructive}
              isLoading={isLoading}
              isDisabled={isConfirmDisabled}
              onPress={handleConfirm}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
});

ConfirmPopover.displayName = "ConfirmPopover";
